/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { UsersService } from '../../../features/authentication/services/api/users.service';
import { HTTP_RESPONSE_ERROR_MESSAGE, SESSION_EXPIRED_ERROR_MESSAGE } from '../../constants/error.constant';
import { StorageService } from '../../services/storage.service';
import { Router } from '@angular/router';
import { ErrorService } from '../../services/error.service';
import {
    AUTHORIZATION_REQUIRED_NEW_DEVICE_LOGIN_DETECTED,
    ERR_ACCOUNT_LOCKED,
    ERR_PASSWORD_EMPTY,
    ERR_FAILED_PROGRAM_REGISTRATION,
    ERR_REQUEST_RECENTLY,
    ERR_USER_INSUFFICIENT_AAE_MINUTES,
    ERR_USER_AAE_BANNED_SUBSCRIPTION_ENDING,
    ERR_USER_EXCEEDED_DEVICE_LIMIT,
    ERR_RESEND_EMAIL_COOLDOWN,
    ERR_OTP_VERIFICATION_RATE_LIMIT_EXCEEDED,
    ERR_USER_ALREADY_COMPLETE_EMAIL_VERIFY,
    ERR_ASKPOD_NOT_ELIGIBLE,
    ERR_ASKPOD_CONNECT_FAILED
} from '../../constants/app.constant';

@Injectable()
export class HttpResponseInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    constructor(
        private errorService: ErrorService,
        private router: Router,
        private storageService: StorageService,
        private userService: UsersService,
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                // Skip error handling for verifyEmail endpoint - let component handle SSL/network errors
                // This allows the component to check if verification succeeded despite network issues
                if (request.url.includes('/verifyEmail')) {
                    return throwError(() => error);
                }

                const apiErrorMessage = this.getApiErrorMessage(error);

                if (error.status === 401 && (!request.params || !request.params.get('refresh'))) {
                    this.errorService.is401ErrorTriggered.next(true);

                    if (error?.error?.error?.code === AUTHORIZATION_REQUIRED_NEW_DEVICE_LOGIN_DETECTED) {
                        const errorMessage = 'error.multipleDeviceLoginDetected';

                        this.errorService.isMultipleDeviceLoginDetected.next(true);

                        return throwError(() => new Error(errorMessage));
                    }

                    return this.handle401Error$(request, next);
                }
                else if (error.status === 404) {
                    return throwError(() => new Error(apiErrorMessage));
                }
                else {
                    if (error?.error?.error?.code === ERR_FAILED_PROGRAM_REGISTRATION) {
                        return throwError(() => error);
                    }
                    else if (error?.error?.error?.code === ERR_RESEND_EMAIL_COOLDOWN) {
                        return throwError(() => error);
                    }
                    else if (error?.error?.error?.code === ERR_USER_ALREADY_COMPLETE_EMAIL_VERIFY) {
                        return throwError(() => error);
                    }
                    else if (error?.error?.error?.code === ERR_ACCOUNT_LOCKED) {
                        return throwError(() => new Error(ERR_ACCOUNT_LOCKED));
                    }
                    else if (error?.error?.error?.code === ERR_PASSWORD_EMPTY) {
                        return throwError(() => new Error(ERR_PASSWORD_EMPTY));
                    }
                    else if (error?.error?.error?.code === ERR_REQUEST_RECENTLY) {
                        return throwError(() => new Error(ERR_REQUEST_RECENTLY));
                    }
                    else if (error?.error?.error?.code === ERR_USER_INSUFFICIENT_AAE_MINUTES) {
                        return throwError(() => new Error(ERR_USER_INSUFFICIENT_AAE_MINUTES));
                    }
                    else if (error?.error?.error?.code === ERR_USER_AAE_BANNED_SUBSCRIPTION_ENDING) {
                        return throwError(() => new Error(ERR_USER_AAE_BANNED_SUBSCRIPTION_ENDING));
                    }
                    else if (
                        error?.error?.error?.code === ERR_USER_EXCEEDED_DEVICE_LIMIT ||
                        error?.error?.error?.code === ERR_OTP_VERIFICATION_RATE_LIMIT_EXCEEDED
                    ) {
                        // Pass the entire error object for device limit exceeded
                        // This allows the login component to access device data from error response
                        return throwError(() => error);
                    }
                    else if (
                        error?.error?.error?.code === ERR_ASKPOD_NOT_ELIGIBLE ||
                        error?.error?.error?.code === ERR_ASKPOD_CONNECT_FAILED
                    ) {
                        // Pass the raw HttpErrorResponse through so the header component can read
                        // error.error.error.code / error.status and show the correct askPOD modal
                        // (Trial Ended vs Connection Down). Without this the error is replaced by a
                        // plain Error below, both fields are lost, and every askPOD failure falls
                        // through to the generic toast.
                        return throwError(() => error);
                    }
                    else {
                        return throwError(() => new Error(apiErrorMessage));
                    }
                }
            }),
        );
    }

    private getApiErrorMessage(error: HttpErrorResponse): string {
        // Debug Log (Error)
        // console.log("[On API Error] Message: ", error);

        if (error.status === 401) {
            return '';
        }

        // Handle network errors and missing error structure gracefully
        try {
            const apiErrorMessage = error?.error?.error?.messages?.[0]?.message || HTTP_RESPONSE_ERROR_MESSAGE;
            return apiErrorMessage;
        }
        catch (e) {
            // Fallback for network errors or malformed error responses
            return error.message || HTTP_RESPONSE_ERROR_MESSAGE;
        }
    }

    private handle401Error$(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const rememberMe = this.storageService.rememberMe;
        const currentUrl = this.router.url;
        const isOnVerifyEmailPage = currentUrl.includes('/verify-email');

        // If the user has never authenticated (no tokens at all), a 401 is not
        // a session-expiry — it's just an auth-required response from a public
        // page hitting a protected resource. Surface the error without clearing
        // storage or kicking them to /login.
        if (!this.storageService.accessToken && !this.storageService.refreshToken) {
            return throwError(() => new Error(SESSION_EXPIRED_ERROR_MESSAGE));
        }

        // FIX: Don't redirect to login if user is on verify-email page
        // The verify-email page should work regardless of session validity
        if (isOnVerifyEmailPage) {
            return throwError(() => {
                return new Error(SESSION_EXPIRED_ERROR_MESSAGE);
            });
        }

        if (rememberMe === 'false') {
            this.storageService.clear();
            this.router.navigate(['/login']);

            return throwError(() => {
                return new Error(SESSION_EXPIRED_ERROR_MESSAGE);
            });
        }

        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return this.userService.refreshToken().pipe(
                switchMap((resp) => {
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(resp.accessToken);

                    return next.handle(this.addAuthenticationToken(request, resp.accessToken));
                }),
                catchError(() => {
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(null);

                    this.storageService.clear();
                    this.router.navigate(['/login']);

                    return throwError(() => {
                        return new Error(SESSION_EXPIRED_ERROR_MESSAGE);
                    });
                }),
            );
        }
        else {
            return this.refreshTokenSubject.pipe(
                filter((token) => token !== null),
                take(1),
                switchMap((token) => {
                    if (!token) {
                        this.storageService.clear();
                        this.router.navigate(['/login']);
                    }

                    return next.handle(this.addAuthenticationToken(request, token));
                }),
            );
        }
    }

    private addAuthenticationToken(request: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`,
            },
        });
    }
}
